import { IVirtualCategory } from "../../contexts/ConfigContext/types";
import { addDatadogError } from "../../services/datadog";

/**
 * Per FOOD-2476 spec: virtual category tag_key must match this regex.
 *   - lowercase a-z, digits 0-9, and hyphen `-` only
 *   - prefix `vc-` is mandatory
 *   - at least one body character after the prefix
 */
const TAG_KEY_REGEX = /^vc-[a-z0-9-]+$/;

type InvalidReason = "missing-prefix" | "malformed-charset" | "empty-string" | "duplicate-tag_key";

const classifyTagKey = (tagKey: string): InvalidReason | "valid" => {
	if (!tagKey) return "empty-string";
	if (!tagKey.startsWith("vc-")) return "missing-prefix";
	if (!TAG_KEY_REGEX.test(tagKey)) return "malformed-charset";

	return "valid";
};

const reportInvalid = (reason: InvalidReason, vc: IVirtualCategory, brand_uuid: string): void => {
	addDatadogError(new Error(`Invalid virtual_category entry: ${reason}`), {
		reason,
		vc,
		brand_uuid,
	});
};

/**
 * Validates a raw virtual_category array from brand config.
 *
 * Behaviour (per FOOD-2476 AC):
 *   - undefined / null / non-array input → returns `[]` silently (no Datadog emission).
 *   - Each entry whose `tag_key` fails the regex or is empty is dropped and triggers
 *     exactly one Datadog error with `{ reason, vc, brand_uuid }` context.
 *   - Duplicate `tag_key` within the array: first occurrence is kept, every subsequent
 *     occurrence is dropped and triggers one Datadog error with reason
 *     `duplicate-tag_key`.
 *   - Valid entries are returned in source order.
 */
export const validateVirtualCategories = (
	raw: IVirtualCategory[] | undefined,
	brand_uuid: string
): IVirtualCategory[] => {
	if (!raw || !Array.isArray(raw) || raw.length === 0) {
		return [];
	}

	const seen = new Set<string>();
	const valid: IVirtualCategory[] = [];

	raw.forEach((vc) => {
		const tagKey = vc?.tag_key ?? "";
		const verdict = classifyTagKey(tagKey);

		if (verdict !== "valid") {
			reportInvalid(verdict, vc, brand_uuid);

			return;
		}

		if (seen.has(tagKey)) {
			reportInvalid("duplicate-tag_key", vc, brand_uuid);

			return;
		}

		seen.add(tagKey);
		valid.push(vc);
	});

	return valid;
};
